import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { FeatureDispatch } from 'features/assetViz/objects/features/FeatureDispatch'
import {
  cartesianCircleGeometry,
  sphereGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoAssetModel, GeckoTorisphericalHead } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a torispherical head - a dome-shaped end cap
 * for pressure vessels featuring a domed crown with a knuckle radius
 * transition to straight flanges. Similar to hemispherical but with
 * more complex geometry including crown and knuckle radii.
 *
 * @param model       The torispherical head component model
 * @param assetModel  The asset model containing this head
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const TorisphericalHeadSurface3D: React.FC<
  {
    model: GeckoTorisphericalHead
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, plates } = React.useMemo(() => {
    const bounds = model.componentBounds

    // Torispherical heads use crown radius for width/depth, but are much flatter
    // Create characteristic dish-like shape with crown and knuckle radius proportions
    // Height is typically much smaller than crown radius to create the flattened appearance
    const torisphericalHeight = model.crownRadius.num * 0.25 // Much flatter than hemisphere
    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(
        model.crownRadius.num, // Width (X-axis) - full crown radius
        torisphericalHeight, // Height (Y-axis) - flattened for dish shape
        model.crownRadius.num // Depth (Z-axis) - full crown radius
      )
    )

    return {
      bounds,
      scale,
      plates: model.platesArray,
    }
  }, [model, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return cartesianCircleGeometry
    return sphereGeometry // Use sphere geometry for dome shape
  }, [show3D])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      <FeatureDispatch features={plates} />
    </ModelSurface3D>
  )
}
